import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { userExist } from "../redux/features/userSlice";
import { message } from "antd";
import Layout from "../components/Layout/Layout";
import "./css/Register.css";
import { completeRegistration, sendOtp, verifyOtp } from "../services/user.service";
import MarketingConsentCheckbox from "../components/MarketingConsent/MarketingConsentCheckbox";
import TermsConsentCheckbox from "../components/MarketingConsent/TermsConsentCheckbox";

const Login = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [form, setForm] = useState({});
	const allowedReferralSources = ["Google","Facebook","Instagram","YouTube","Friend","Other"];
	const [showOtpField, setShowOtpField] = useState(false);
	const [loading, setLoading] = useState(false);
	const [tab, setTab] = useState(0);
	const [mobileValidation, setMobileValidation] = useState({
		isValid: null, // null = no validation, true = valid, false = invalid
		message: ""
	});

	const handleChange = (e) => {
		const { name, value } = e.target;
		
		// For mobile number, only allow digits and limit to 10 characters
		if (name === 'mobile') {
			const numericValue = value.replace(/\D/g, ''); // Remove non-digits
			if (numericValue.length <= 10) {
				setForm(prev => ({ ...prev, [name]: numericValue }));
				
				// Real-time validation feedback
				const indianMobileRegex = /^[6-9]\d{9}$/;
				
				if (numericValue.length === 0) {
					setMobileValidation({ isValid: null, message: "" });
				} else if (numericValue.length < 10) {
					// Check if it starts with valid Indian prefix
					if (/^[6-9]/.test(numericValue)) {
						setMobileValidation({ 
							isValid: true, 
							message: "Complete 10 digit mobile number" 
						});
					} else {
						setMobileValidation({ 
							isValid: false, 
							message: "Invalid number, only Indian number supports" 
						});
					}
				} else if (numericValue.length === 10) {
					if (indianMobileRegex.test(numericValue)) {
						setMobileValidation({ 
							isValid: true, 
							message: "Valid Indian mobile number" 
						});
					} else {
						setMobileValidation({ 
							isValid: false, 
							message: "Invalid Indian mobile number format" 
						});
					}
				}
			}
		} else {
			setForm(prev => ({ ...prev, [name]: value }));
		}
	};

	const sendMobileOtp = async (e) => {
		e.preventDefault();

		if (form?.mobile?.length !== 10) {
			return message.error("Enter 10 digits mobile number");
		}

		// Indian mobile number validation
		const indianMobileRegex = /^[6-9]\d{9}$/;
		if (!indianMobileRegex.test(form?.mobile)) {
			return message.error("Please enter a valid Indian mobile number (starting with 6, 7, 8, or 9)");
		}

		setLoading(true);
		try {
			const data = await sendOtp(form.mobile);
			if (data) {
				message.success(data.message);
				setShowOtpField(true);
			} else {
				message.error(data.message);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (form?.mobile?.length !== 10) {
			return message.error("Enter 10 digits mobile number");
		}

		// Indian mobile number validation
		const indianMobileRegex = /^[6-9]\d{9}$/;
		if (!indianMobileRegex.test(form?.mobile)) {
			return message.error("Please enter a valid Indian mobile number (starting with 6, 7, 8, or 9)");
		}

		if (!form?.otp) {
			return message.error("Please enter 6 digits OTP");
		}
		if (form?.otp?.length !== 6) {
			return message.error("Enter 6 digits OTP");
		}

		try {
			const data = await verifyOtp(form.mobile, form.otp);
			if (data) {
				if (data.isNewUser || data.requiresRegistration) {
					if (data.phone) {
						setForm((prev) => ({ ...prev, mobile: data.phone }));
					}
					if (data.registrationToken) {
						setForm((prev) => ({ ...prev, registrationToken: data.registrationToken }));
					}
					setTab(1);
				} else {
					localStorage.setItem("token", data.token);
					dispatch(userExist(data.user));
					navigate("/");
				}
			} else {
				message.error(data.message);
			}
		} catch (error) {
			console.log(error.message);
			message.error("Something went wrong");
		}
	};

	const handleProfieUpdate = async (e) => {
		e.preventDefault();
		
		// Indian mobile number validation
		const indianMobileRegex = /^[6-9]\d{9}$/;
		if (!indianMobileRegex.test(form?.mobile)) {
			return message.error("Please enter a valid Indian mobile number (starting with 6, 7, 8, or 9)");
		}
		
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (form?.email === "" || form?.fname === "" || form?.password === "") {
			return message.error(
				`Please Enter ${form?.fname === "" ? "your name" : form?.email === "" ? "your email" : "a password"}`
			);
		}
		if (!emailRegex.test(form?.email)) {
			return message.error("Please enter a valid email address");
		}
		if (form?.fname?.length < 5) {
			return message.error("Name should be more than 5 characters");
		}
		if (form?.password?.length < 5) {
			return message.error("password should be at least 5 characters");
		}

		if (!form?.termsAccepted) {
			return message.error("Please accept the Terms & Conditions and Privacy Policy");
		}

		setLoading(true);

		try {
			const data = await completeRegistration(
				form.mobile,
				form.fname,
				form.email,
				form.password,
				form?.referralSource,
				form?.registrationToken,
				!!form?.marketingConsent
			)
			if (data) {
				localStorage.setItem("token", data.token);
				message.success(data.message);
				dispatch(userExist(data.user));
				navigate("/");
			} else {
				message.error(data.message);
			}
		} catch (error) {
			console.log(error);
			message.error("Something went wrong");
		}
	};

	return (
		<Layout>
			<div className="container-fluid hero-container register-container">
				<div className="row text-center w-100">
					<div className="d-block m-auto col-12 col-sm-12 col-md-6 col-lg-6">
						{tab === 0 && (
							<div className="register-form">
								<h1>Welcome Back!</h1>
								<div className="form-fields mb-3 position-relative">
									<input
										onChange={handleChange}
										value={form?.mobile}
										name="mobile"
										type="tel"
										className={`form-control ${
											mobileValidation.isValid === true && form?.mobile?.length === 10 ? 'is-valid' : 
											mobileValidation.isValid === true && form?.mobile?.length < 10 ? 'border-warning' :
											mobileValidation.isValid === false ? 'is-invalid' : ''
										}`}
										placeholder="Enter Registered Mobile Number"
										maxLength={10}
										pattern="[0-9]{10}"
										title="Please enter a 10-digit mobile number"
									/>
									{/* Custom validation icons */}
									{mobileValidation.isValid === true && form?.mobile?.length === 10 && (
										<div className="position-absolute top-50 end-0 translate-middle-y me-3">
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
											</svg>
										</div>
									)}
									{mobileValidation.isValid === true && form?.mobile?.length < 10 && form?.mobile?.length > 0 && (
										<div className="position-absolute top-50 end-0 translate-middle-y me-3">
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="#ffc107"/>
											</svg>
										</div>
									)}
									{mobileValidation.isValid === false && form?.mobile?.length > 0 && (
										<div className="position-absolute top-50 end-0 translate-middle-y me-3">
											<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" fill="#dc3545"/>
											</svg>
										</div>
									)}
									{mobileValidation.message && (
										<div className={`${
											mobileValidation.isValid === true && form?.mobile?.length === 10 ? 'valid-feedback' : 
											mobileValidation.isValid === true && form?.mobile?.length < 10 ? 'text-warning' :
											'invalid-feedback'
										} d-block`}>
											{mobileValidation.message}
										</div>
									)}
								</div>
								{showOtpField && (
									<div className="form-fields mb-3">
										<input
											onChange={handleChange}
											value={form?.otp}
											name="otp"
											type="number"
											className="form-control"
											placeholder="Enter Verification OTP"
										/>
									</div>
								)}
								{!showOtpField && (
									<button onClick={sendMobileOtp} className="register-btn">
										Send OTP
										{loading && (
											<div
												className="ms-1 spinner-grow spinner-grow-sm"
												role="status"
											>
												<span className="visually-hidden">Loading...</span>
											</div>
										)}
									</button>
								)}
								{showOtpField && (
									<button onClick={handleSubmit} className="register-btn">
										Verify OTP
										{loading && (
											<div
												className="ms-1 spinner-grow spinner-grow-sm"
												role="status"
											>
												<span className="visually-hidden">Loading...</span>
											</div>
										)}
									</button>
								)}
								<div className="forgot-pass d-flex justify-content-between"></div>
							</div>
						)}

						{tab === 1 && (
							<div className="register-form">
								<h1>Update Profile</h1>
								<div className="form-fields mb-3">
									<input
										onChange={handleChange}
										value={form?.fname}
										name="fname"
										type="text"
										className="form-control"
										placeholder="Enter your name"
									/>
								</div>
								<div className="form-fields mb-3">
									<input
										onChange={handleChange}
										value={form?.email}
										name="email"
										type="text"
										className="form-control"
										placeholder="Enter your email"
									/>
								</div>
							<div className="form-fields mb-3">
								<select
									name="referralSource"
									className="form-control"
									value={form?.referralSource || ""}
									onChange={handleChange}
								>
									<option value="" disabled>
										How did you hear about us?
									</option>
									{allowedReferralSources.map((source) => (
										<option key={source} value={source}>
											{source}
										</option>
									))}
								</select>
							</div>
								<div className="form-fields mb-3">
									<input
										onChange={handleChange}
										value={form?.password}
										name="password"
										type="text"
										className="form-control"
										placeholder="Enter your password"
										minLength={5}
									/>
								</div>
								<div className="form-fields mb-3">
									<MarketingConsentCheckbox
										checked={form?.marketingConsent}
										onChange={(checked) => setForm((prev) => ({ ...prev, marketingConsent: checked }))}
									/>
									<TermsConsentCheckbox
										className="mt-2"
										checked={form?.termsAccepted}
										onChange={(checked) => setForm((prev) => ({ ...prev, termsAccepted: checked }))}
									/>
								</div>
								<button onClick={handleProfieUpdate} className="register-btn">
									Update Profile
									{loading && (
										<div
											className="ms-1 spinner-grow spinner-grow-sm"
											role="status"
										>
											<span className="visually-hidden">Loading...</span>
										</div>
									)}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</Layout>
	);
};

export default Login;
